import { ChipProps } from 'bento';
import { Theme, css } from '@emotion/react';

/**
 * Applies font and borderRadius values
 */
export const chipBase = ({ borderRadius, spacing, typography }: Theme, isIconOnly: boolean) => {
  return [
    css`
      ${typography.variant('overline')};
      border-radius: ${borderRadius[100]};
      border-style: solid;
      border-width: 1px;
      height: ${spacing(2.5)}px;
      padding: 0 ${spacing(0.5)}px;
      gap: ${spacing(0.5)}px;

      .MuiChip-label,
      .MuiChip-labelSmall {
        padding: 0;
      }

      .MuiChip-icon,
      .MuiChip-iconSmall {
        width: auto;
        height: auto;
        font-size: ${typography.pxToRem(16)};
        margin: 0;
      }

      .MuiChip-deleteIcon {
        color: inherit;
      }
    `,
    isIconOnly &&
      css`
        max-width: '20px';

        .MuiChip-label {
          display: none;
        }
      `,
  ];
};

const getChipColor = (colors: Theme['colors'], color: ChipProps['color']) => {
  switch (color) {
    case 'primary':
      return css`
        --chip-background-color: ${colors.bgPrimaryLighter};
        --chip-border-color: ${colors.borderPrimaryLight};
        --chip-text-color: ${colors.fgPrimaryDark};
        --chip-hover-color: ${colors.bgPrimaryLight};
      `;
    case 'subtle':
      return css`
        --chip-background-color: ${colors.bgLighter};
        --chip-border-color: ${colors.borderDefault};
        --chip-text-color: ${colors.fgDefault};
        --chip-hover-color: ${colors.bgLight};
      `;
    case 'success':
      return css`
        --chip-background-color: ${colors.bgSuccessLighter};
        --chip-border-color: ${colors.borderSuccessLight};
        --chip-text-color: ${colors.fgSuccessDark};
        --chip-hover-color: ${colors.bgSuccessLight};
      `;
    case 'warning':
      return css`
        --chip-background-color: ${colors.bgWarningLighter};
        --chip-border-color: ${colors.borderWarningLight};
        --chip-text-color: ${colors.fgWarningDark};
        --chip-hover-color: ${colors.bgWarningLight};
      `;
    case 'danger':
      return css`
        --chip-background-color: ${colors.bgDangerLighter};
        --chip-border-color: ${colors.borderDangerLight};
        --chip-text-color: ${colors.fgDangerDark};
        --chip-hover-color: ${colors.bgDangerLight};
      `;
    case 'internal':
      return css`
        --chip-background-color: ${colors.bgInternalLighter};
        --chip-border-color: ${colors.borderInternalLight};
        --chip-text-color: ${colors.fgInternalDark};
        --chip-hover-color: ${colors.bgInternalLight};
      `;
    case 'featureGate':
      return css`
        --chip-background-color: ${colors.bgFeatureGateLighter};
        --chip-border-color: ${colors.borderFeatureGateLight};
        --chip-text-color: ${colors.fgFeatureGateDark};
        --chip-hover-color: ${colors.bgFeatureGateLight};
      `;
    case 'gda':
      return css`
        --chip-background-color: ${colors.bgGdaLighter};
        --chip-border-color: ${colors.borderGdaLight};
        --chip-text-color: ${colors.fgGdaDark};
        --chip-hover-color: ${colors.bgGdaLight};
      `;
  }
};

export const chipColor = ({ colors, interactiveStates }: Theme, color: ChipProps['color']) => {
  const chipColorVars = getChipColor(colors, color);

  return css`
    ${chipColorVars}

    background-color: var(--chip-background-color);
    border-color: var(--chip-border-color);
    color: var(--chip-text-color);

    .MuiChip-icon {
      fill: var(--chip-text-color);
    }

    &.MuiChip-clickable {
      ${interactiveStates.hover(css`
        background-color: var(--chip-hover-color);
      `)}

      ${interactiveStates.focus(css`
        background-color: var(--chip-background-color);
        outline: 2px solid ${colors.borderPrimaryBase};
        outline-offset: 2px;
      `)}
    }
  `;
};
